import { Injectable, inject, signal } from '@angular/core';
import { PortalContextService } from './portal-context.service';

const LEGACY_ACCESS_TOKEN_KEY = 'zepbazaar_access_token';
const LEGACY_REFRESH_TOKEN_KEY = 'zepbazaar_refresh_token';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private readonly portal = inject(PortalContextService);
  private readonly accessTokenKey = this.portal.storageKey('access_token');
  private readonly refreshTokenKey = this.portal.storageKey('refresh_token');
  readonly accessToken = signal<string | null>(this.read(this.accessTokenKey));

  setTokens(accessToken: string, refreshToken?: string): void {
    this.write(this.accessTokenKey, accessToken);
    this.accessToken.set(accessToken);

    if (refreshToken) {
      this.write(this.refreshTokenKey, refreshToken);
    }
  }

  getAccessToken(): string | null {
    return this.accessToken();
  }

  getRefreshToken(): string | null {
    return this.read(this.refreshTokenKey);
  }

  getTenantId(): string | null {
    const token = this.getAccessToken();
    if (!token) {
      return null;
    }

    try {
      const encodedPayload = token.split('.')[1];
      if (!encodedPayload) {
        return null;
      }

      const base64Payload = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
      const normalizedPayload = base64Payload.padEnd(Math.ceil(base64Payload.length / 4) * 4, '=');
      const payload = JSON.parse(atob(normalizedPayload)) as Record<string, unknown>;
      const tenantId = payload['tenant_id'] ?? payload['tenantId'];
      return typeof tenantId === 'string' && tenantId.trim() ? tenantId : null;
    } catch {
      return null;
    }
  }

  clear(): void {
    this.remove(this.accessTokenKey);
    this.remove(this.refreshTokenKey);
    // Remove old shared keys so a legacy session cannot leak into a dedicated portal host.
    this.remove(LEGACY_ACCESS_TOKEN_KEY);
    this.remove(LEGACY_REFRESH_TOKEN_KEY);
    this.accessToken.set(null);
  }

  private read(key: string): string | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    return localStorage.getItem(key);
  }

  private write(key: string, value: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(key, value);
    }
  }

  private remove(key: string): void {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(key);
    }
  }
}
